import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { useSectionContent } from "@/hooks/useContent";

const INFO_ICONS = { city: MapPin, email: Mail, instagram: Instagram, facebook: Facebook };
const INFO_LABELS = { city: "Clubhouse", email: "Email", instagram: "Instagram", facebook: "Facebook" };

export default function Contacts() {
  const c = useSectionContent("contacts");
  const info = ["city", "email", "instagram", "facebook"].map((k) => ({
    icon: INFO_ICONS[k],
    label: INFO_LABELS[k],
    value: c[k],
  }));
  const [form, setForm] = useState({ name: "", email: "", bike: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacts", form);
      toast.success("Messaggio inviato. Ti risponderemo presto!");
      setForm({ name: "", email: "", bike: "", message: "" });
    } catch {
      toast.error("Invio non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="contacts-section" className="py-24 sm:py-32 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading
            overline={c.overline}
            title={c.title}
            description={c.description}
            testid="contacts-heading"
          />
          <div className="mt-10 space-y-4">
            {info.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center gap-4"
                data-testid={`contact-info-${item.label.toLowerCase()}`}
              >
                <div className="w-11 h-11 border border-[#C5A059]/30 flex items-center justify-center shrink-0">
                  <item.icon className="w-4 h-4 text-[#C5A059]" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D]">
                    {item.label}
                  </p>
                  <p className="text-sm text-[#F4EBE1]">{item.value}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="leather-card p-7 space-y-5 self-start"
          data-testid="contact-form"
        >
          {[
            { name: "name", label: "Nome e Cognome", type: "text", testid: "contact-name-input" },
            { name: "email", label: "Email", type: "email", testid: "contact-email-input" },
            { name: "bike", label: "Modello Moto / Club (opzionale)", type: "text", testid: "contact-bike-input", optional: true },
          ].map((f) => (
            <div key={f.name}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
                {f.label}
              </label>
              <input
                data-testid={f.testid}
                required={!f.optional}
                type={f.type}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] transition-colors"
              />
            </div>
          ))}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
              Messaggio
            </label>
            <textarea
              data-testid="contact-message-input"
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] transition-colors resize-none"
            />
          </div>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {loading ? "Invio in corso..." : "Invia Messaggio"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
